import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/lib/constants";
import { CommunityMap } from "@/components/CommunityMap";
import { motion } from "framer-motion";
import { AlertTriangle, Landmark, Eye, Megaphone, FileSearch, ScrollText, ArrowRight, ArrowRightLeft } from "lucide-react";

const FAILURES = [
  { icon: ScrollText, t: "No Implementing Legislation", d: "Although Article VI makes ICERD part of the supreme law of the land, the U.S. ratified it as 'non-self-executing' and never passed legislation giving its provisions domestic legal effect — so victims cannot enforce CERD rights in U.S. courts." },
  { icon: FileSearch, t: "No Systematic Review", d: "The U.S. lacks any national mechanism to systematically review government policies and practices for CERD compliance or to monitor levels of protection — including at the state and local levels." },
  { icon: Landmark, t: "No State/Local Oversight", d: "Despite the federalism understanding obligating the federal government to ensure compliance everywhere, the U.S. has failed to oversee, coordinate, and facilitate CERD compliance at the state and local levels." },
  { icon: Megaphone, t: "No Public Awareness", d: "The U.S. has not educated officials, judges, or the public about CERD's guarantees — implementation depends on public knowledge that has never been cultivated." },
  { icon: Eye, t: "No Review of Reservations (RUDs)", d: "The U.S. has not publicly reviewed its reservations, understandings, and declarations to determine their continuing necessity or relevance, despite a standing mandate to do so." },
];

const SOLUTIONS = [
  { t: "A Standing Human-Rights Body", d: "Establish a Nation institution — an Office of Human Rights & Compliance — that monitors racial-equity conditions affecting our people and tracks federal, state, and local compliance with CERD Article 2, mirroring the national monitoring mechanism the U.S. itself lacks." },
  { t: "Shadow / Alternative Reporting", d: "Compile and submit alternative ('shadow') reports to the UN CERD Committee — exactly the civil-society model pioneered by Berkeley and San Francisco — documenting disparities our members experience and the absence of remedies." },
  { t: "Member Data & Disparity Tracking", d: "Use member enrollment data (skills, region, residence) to assemble disaggregated statistics on conditions and disparities — the very data the U.S. has failed to collect — to evidence patterns of discrimination." },
  { t: "Public Education & Know-Your-Rights", d: "Run education campaigns and trainings so members, officials, and the public understand CERD's guarantees — directly filling the awareness gap the Committee identified." },
  { t: "Model Legislation Advocacy", d: "Draft and advocate for state and local resolutions and implementing measures (as in Massachusetts, Pennsylvania, and New York City) that incorporate CERD's definition of discrimination into law." },
  { t: "Committees as Implementation Engines", d: "Channel the work through the Nation's committees — Justice & Legal Affairs, Reparations, Media & Communications — turning each CERD obligation into an organized program of action." },
];

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function CERDObligations() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-24 grid lg:grid-cols-3 gap-12 items-center">
        <div className="lg:col-span-2">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">Treaty Accountability</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.98] text-[#F5F3EF]">
            CERD Obligations &<br />Our Path to Implementation
          </h1>
          <p className="mt-7 text-lg text-[#A8A29E] leading-relaxed max-w-2xl">
            When the United States ratified the <span className="text-[#F5F3EF] font-medium">International Convention on the
            Elimination of All Forms of Racial Discrimination (CERD)</span> on October 21, 1994, it bound itself under
            Article 2 to eliminate racial discrimination "by all appropriate means and without delay." A generation later,
            those obligations remain largely unimplemented at home. This is where the Indigenous Black American Nation can act.
          </p>
        </div>
        <div className="hidden lg:flex justify-center">
          <img src={ASSETS.seal} alt="National Seal" className="h-56 w-56 object-contain" />
        </div>
      </section>

      {/* Community footprint map */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-4">
        <CommunityMap heading="The People CERD Protects" subheading="Black American communities, neighborhoods, and historic districts across the United States — the people to whom the United States owes its CERD Article 2 obligations." />
      </section>

      {/* What Article 2 requires */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 grid lg:grid-cols-3 gap-12">
          <div>
            <ScrollText className="w-8 h-8 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight">What Article 2 Requires</h2>
          </div>
          <div className="lg:col-span-2 space-y-5 text-[#C9C5BD] leading-relaxed">
            <p>Upon ratification, the United States committed under CERD Article 2 to:</p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-3"><span className="text-[#D4AF37]">▸</span> Engage in no act or practice of racial discrimination, and ensure all public authorities — <span className="text-[#F5F3EF]">national and local</span> — act in conformity.</li>
              <li className="flex gap-3"><span className="text-[#D4AF37]">▸</span> Prohibit and bring to an end racial discrimination by any person, group, or organization.</li>
              <li className="flex gap-3"><span className="text-[#D4AF37]">▸</span> Take effective measures to <span className="text-[#F5F3EF]">review</span> national and local policies and to amend, rescind, or nullify any laws that create or perpetuate racial discrimination.</li>
            </ul>
            <div className="doc-scroll border border-[#D4AF37]/30 p-6 font-serif-display text-lg text-[#EDE9E1] leading-relaxed">
              "…to ensure that all public authorities and public institutions, national and local, shall act in conformity
              with this obligation… and to amend, rescind or nullify any laws and regulations which have the effect of
              creating or perpetuating racial discrimination wherever it exists."
              <span className="block mt-3 text-sm text-[#A8A29E] font-sans">— ICERD, Article 2(1)</span>
            </div>
            <p className="text-sm text-[#A8A29E]">
              The U.S. attached a "federalism understanding," but acknowledges this "is not a reservation" and "cannot
              serve as an excuse for any failure to comply." Through the Supremacy Clause, the obligation reaches every level of government.
            </p>
          </div>
        </div>
      </section>

      {/* The Failures */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-[#C8102E]" />
          <div className="text-xs tracking-[0.25em] uppercase text-[#C8102E] font-semibold">The Compliance Gap</div>
        </div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">Five U.S. Failures Under CERD</h2>
        <p className="text-[#A8A29E] max-w-3xl mb-12">U.S. civil society reported five systemic failures to the CERD Committee. Each traces to the absence of any permanent body to monitor treaty implementation across the nation.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FAILURES.map((f, i) => (
            <motion.div key={f.t} {...FADE} transition={{ duration: 0.5, delay: i * 0.05 }} className="bg-[#141414] border border-[#C8102E]/30 p-7">
              <f.icon className="w-7 h-7 text-[#C8102E] mb-4" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-2 leading-snug">{f.t}</h3>
              <p className="text-sm text-[#A8A29E] leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Bridge */}
      <section className="border-y border-[#D4AF37]/20 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
          <ArrowRightLeft className="w-10 h-10 text-[#D4AF37] shrink-0" strokeWidth={1.5} />
          <p className="font-serif-display text-2xl sm:text-3xl text-[#F5F3EF] leading-snug">
            Where the State has left a vacuum, an organized people can build the very mechanisms the treaty demands.
          </p>
        </div>
      </section>

      {/* Solutions */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20">
        <div className="text-xs tracking-[0.25em] uppercase text-[#5DBB7A] font-semibold mb-3">Our Response</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F5F3EF] tracking-tight mb-4">How the Indigenous Black American Nation Can Address These Issues</h2>
        <p className="text-[#A8A29E] max-w-3xl mb-12">Acting lawfully and peacefully as a self-organized people, the Nation can establish the monitoring, reporting, and advocacy structures the United States has failed to create.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SOLUTIONS.map((s, i) => (
            <motion.div key={s.t} {...FADE} transition={{ duration: 0.5, delay: i * 0.05 }} className="bg-[#141414] border border-[#5DBB7A]/40 p-7">
              <div className="font-mono-doc text-[#5DBB7A] text-sm mb-3">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-serif-display text-2xl text-[#F5F3EF] mb-2 leading-snug">{s.t}</h3>
              <p className="text-sm text-[#A8A29E] leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA + disclaimer */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pb-20">
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-10 sm:p-14 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F3EF] tracking-tight max-w-2xl mx-auto">Help us build the accountability our people deserve.</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/register" data-testid="cerd-register-btn">
              <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6">Become a Member <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
            <Link to="/legal-standing" data-testid="cerd-to-legal-btn">
              <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">Our Legal Standing</Button>
            </Link>
          </div>
        </div>
        <p className="text-xs text-[#A8A29E]/70 leading-relaxed mt-8 max-w-4xl mx-auto text-center">
          This page summarizes the U.S. Human Rights Network's civil-society report to the UN CERD Committee (Article 2,
          2007–2008) and related public instruments for educational purposes. It is not legal advice. Any advocacy is
          intended to be pursued through lawful and peaceful means.
        </p>
      </section>

      <Footer />
    </div>
  );
}
